import { unwrap, type components } from "@wilco/api";
import { api } from "../auth.ts";

/*
 * Себестоимость на REST из contracts/openapi.json (модуль costing), всё под
 * правом cost.read (CST-23): без него сервер отвечает отказом, а меню
 * раздел прячет. Месяц строкой YYYY-MM, деньги в гривне без ПДВ.
 */
type S = components["schemas"];

export type Close = S["CostingClose"];
export type BasisPreview = S["CostingBasisPreview"];
export type Pnl = S["CostingPnl"];
export type PricePoint = S["CostingPricePoint"];
export type LotCost = S["CostingLotCost"];
export type BomLine = S["CostingBomLine"];
export type Contribution = S["CostingContribution"];
export type TrendPoint = S["CostingTrendPoint"];

const month = (m: string) => ({ params: { path: { month: m } } });

export const costingApi = {
  settings: () => unwrap(api.GET("/api/v1/costing/settings")),
  closes: () => unwrap(api.GET("/api/v1/costing/closes")),
  close: (m: string) => unwrap(api.GET("/api/v1/costing/closes/{month}", month(m))),
  draft: (m: string) => unwrap(api.POST("/api/v1/costing/closes/{month}", month(m))),
  deleteDraft: (m: string) => unwrap(api.DELETE("/api/v1/costing/closes/{month}", month(m))).then(() => null),
  basis: (m: string) => unwrap(api.GET("/api/v1/costing/closes/{month}/basis", month(m))),
  setBasis: (m: string, basis: "COEFFICIENT" | "MINUTES") => unwrap(api.PUT("/api/v1/costing/closes/{month}/basis", { ...month(m), body: { basis } })),
  payroll: (m: string, official: number, cash: number) => unwrap(api.PUT("/api/v1/costing/closes/{month}/payroll", { ...month(m), body: { official, cash } })),
  shares: (m: string, ser: number, rnd: number, srv: number) => unwrap(api.PUT("/api/v1/costing/closes/{month}/shares", { ...month(m), body: { ser, rnd, srv } })),
  note: (m: string, text: string) => unwrap(api.POST("/api/v1/costing/closes/{month}/notes", { ...month(m), body: { text } })),
  closeMonth: (m: string) => unwrap(api.POST("/api/v1/costing/closes/{month}/close", month(m))),
  reopen: (m: string, reason: string) => unwrap(api.POST("/api/v1/costing/closes/{month}/reopen", { ...month(m), body: { reason } })),
  pnl: (m: string) => unwrap(api.GET("/api/v1/costing/pnl/{month}", month(m))),
  componentPrices: (product: string) => unwrap(api.GET("/api/v1/costing/prices/components/{product}", { params: { path: { product } } })),
  lot: (lot: string) => unwrap(api.GET("/api/v1/costing/prices/lots/{lot}", { params: { path: { lot } } })),
  trend: (product: string, version: string | null, from: string, to: string) =>
    unwrap(api.GET("/api/v1/costing/prices/trend", { params: { query: { product, version: version ?? undefined, from, to } } })),
  bom: (product: string, version: string | null, lastLot: string) =>
    unwrap(api.GET("/api/v1/costing/prices/bom", { params: { query: { product, version: version ?? undefined, lastLot } } })),
  contribution: (product: string, version: string | null, before: string, after: string) =>
    unwrap(api.GET("/api/v1/costing/prices/contribution", { params: { query: { product, version: version ?? undefined, before, after } } })),
  draftCost: (lines: { product: string; qty: number }[]) => unwrap(api.POST("/api/v1/costing/prices/draft", { body: lines })),
};

/** Месяц YYYY-MM со сдвигом от текущего по часам браузера. */
export function monthOf(offset: number, from = new Date()): string {
  const d = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + offset, 1));
  return d.toISOString().slice(0, 7);
}

/** Мусор в адресе (`2026-13`) даёт текущий месяц, а не ошибку (СВ-14). */
export function validMonth(m: string | undefined, fallback: string): string {
  return m && /^\d{4}-(0[1-9]|1[0-2])$/.test(m) ? m : fallback;
}

const monthFormat = new Intl.DateTimeFormat("uk-UA", { month: "long", year: "numeric", timeZone: "UTC" });
export const formatMonth = (m: string): string => monthFormat.format(new Date(`${m}-01T00:00:00Z`));
